import {AriaCheckboxProps} from '../checkbox/useCheckbox';
import {IGridCollection as GridCollection} from 'react-stately/private/grid/GridCollection';
import {GridState} from 'react-stately/private/grid/useGridState';
import intlMessages from '../../intl/grid/*.json';
import {Key} from '@react-types/shared';
import {useId} from '../utils/useId';
import {useLocalizedStringFormatter} from '../i18n/useLocalizedStringFormatter';

export interface AriaGridSelectionCheckboxProps {
  /** A unique key for the checkbox. */
  key: Key;
}

export interface GridSelectionCheckboxAria {
  /** Props for the row selection checkbox element. */
  checkboxProps: AriaCheckboxProps;
}

/**
 * Provides the behavior and accessibility implementation for a selection checkbox in a grid.
 *
 * @param props - Props for the selection checkbox.
 * @param state - State of the grid, as returned by `useGridState`.
 */
export function useGridSelectionCheckbox<T, C extends GridCollection<T>>(
  props: AriaGridSelectionCheckboxProps,
  state: GridState<T, C>
): GridSelectionCheckboxAria {
  let {key} = props;

  let manager = state.selectionManager;
  let checkboxId = useId();
  let isDisabled = !state.selectionManager.canSelectItem(key);
  let isSelected = state.selectionManager.isSelected(key);

  // Checkbox should always toggle selection, regardless of selectionBehavior.
  let onChange = () => manager.toggleSelection(key);

  const stringFormatter = useLocalizedStringFormatter(intlMessages, '@react-aria/grid');

  return {
    checkboxProps: {
      id: checkboxId,
      'aria-label': stringFormatter.format('select'),
      isSelected,
      isDisabled,
      onChange
    }
  };
}
